'use client'

import { useEffect, useRef, useState } from 'react'

import { Direction, Props } from './types'

const ParallaxSprite = ({
  className = '',
  style = {},
  speed = 1,
  axis = 'xy',
  directionX = Direction.Normal,
  directionY = Direction.Normal,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null)
  const lastScrollY = useRef<number>(0)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    // IntersectionObserver to track visibility
    const observer = new IntersectionObserver(
      ([entry]) => {
        setVisible(entry.isIntersecting)
      },
      {
        threshold: 0.1, // Trigger when 10% visible
      },
    )

    observer.observe(el)

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const onScroll = () => {
      if (!visible) return

      const scrollY = window.scrollY
      const direction = scrollY > lastScrollY.current ? 1 : -1
      lastScrollY.current = scrollY

      let dx = 0
      let dy = 0

      if (axis.includes('x')) {
        const dir = directionX === Direction.Reverse ? -1 : directionX === Direction.Normal ? 1 : 0
        dx = direction * speed * dir * 10 // 10 — base shift
      }

      if (axis.includes('y')) {
        const dir = directionY === Direction.Reverse ? -1 : directionY === Direction.Normal ? 1 : 0
        dy = direction * speed * dir * 10
      }

      const currentTransform = el.style.transform || ''
      const match = currentTransform.match(/translate\(([-\d.]+)px,\s*([-\d.]+)px\)/)
      const currentX = match ? parseFloat(match[1]) : 0
      const currentY = match ? parseFloat(match[2]) : 0

      el.style.transform = `translate(${currentX + dx}px, ${currentY + dy}px)`
    }

    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [visible, speed, axis, directionX, directionY])

  return <div ref={ref} className={className} style={style} />
}

export default ParallaxSprite
